import React, { useEffect, useState } from 'react';
import { Container, ContainerProps, Grid, NoSsr, Theme, useMediaQuery } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import GlobalStyles from '@mui/material/GlobalStyles';
import { getWithExpiry } from '../Header/Headers/utils';

export type BoxContainerV2Props = Pick<ContainerProps, 'maxWidth'> & {
  variant?: 'regular' | 'dense';
  overflow?: boolean;
  frame?: boolean;
  children?: React.ReactNode;
  head?: React.ReactNode;
};

interface StyleProps {
  overflow: boolean;
}

const useStyles = makeStyles<StyleProps>()((theme: Theme, params) => ({
  root: {
    [theme.breakpoints.down('md')]: {
      maxWidth: '100%',
      background: theme.palette.background.paper,
      height: `calc(100% - ${theme.mixins.toolbar.minHeight}px)`,
      '&.dense': {
        height: `calc(100% - ${theme.mixins.appBar.minHeight}px)`,
      },
      '&.overflow': {
        height: 'initial',
      },
      '&:not(.overflow) > *': {
        flexDirection: 'column',
      },
    },
    [theme.breakpoints.up('md')]: {
      position: 'relative',
      height: 'calc(100% - 120px)',
      marginTop: 0,
      borderRadius: 0,
      overflow: params.overflow ? 'visible' : 'hidden',
      '&.frame': {
        height: '100%',
        marginTop: 0,
      },
    },
    '& > *': {
      height: '100%',
    },
  },
  headWrapper: {
    background: theme.palette.background.default,
  },
  contentWrapper: {
    [theme.breakpoints.down('md')]: {
      padding: '0 0',
    },
    [theme.breakpoints.down('sm')]: {
      padding: '0 16px',
    },
  },
}));

const BoxContainerV2: React.FC<React.PropsWithChildren<BoxContainerV2Props>> = (props) => {
  const { head, children, maxWidth = 'md', variant = 'regular', overflow = false, frame = false } = props;
  const less1080 = useMediaQuery('(max-width: 1080px)');
  const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down('md'));
  const [topPosition, setTopPosition] = useState(isMobile ? 64 : 64 + 48);

  useEffect(() => {
    const isBannerHidden = getWithExpiry('mobileAppsBannerHidden');

    if (less1080 && !isMobile) setTopPosition(64 + (isBannerHidden ? 0 : 64));
    if (isMobile) setTopPosition(64);
    if (!less1080 && !isMobile) setTopPosition(64 + (isBannerHidden ? 0 : 48));
  }, [isMobile, less1080]);

  useEffect(() => {
    const handleStorageChange = () => {
      setTopPosition(64);
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const { classes, cx } = useStyles(
    { overflow },
    {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      props,
    },
  );
  return (
    <NoSsr>
      <div style={{ paddingTop: topPosition }}>
        <GlobalStyles
          styles={{
            html: {
              height: '100%',
            },
            body: {
              height: '100%',
              '& > *': {
                height: 'inherit',
              },
              background: '#fff',
            },
          }}
        />

        <div id="eventHeadWrapper" className={classes.headWrapper}>
          <Container className={cx(classes.root, variant, { overflow, frame })} maxWidth={maxWidth} disableGutters>
            <Grid container>{head}</Grid>
          </Container>
        </div>

        <Container className={cx(classes.root, variant, { overflow, frame })} maxWidth={maxWidth} disableGutters>
          <Grid className={classes.contentWrapper} container>
            {children}
          </Grid>
        </Container>
      </div>
    </NoSsr>
  );
};

export default BoxContainerV2;
